import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { get, set, map, reduce, omitBy, isEmpty, some, every } from "lodash"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Typography, Alert } from "@mui/material"
import Item from "./components/item"
import { validTagOrAttributeCategory, validName } from "./helpers"

import "./styles.scss"

function defaultTag(id) {
  return {
    id,
    name: id,
    description: "",
    checked: validTagOrAttributeCategory(id),
    isInvalid: !validTagOrAttributeCategory(id),
  }
}

function validTagKeys(value, key) {
  return isEmpty(value) || key === "checked"
}

export default function TagAndAttributeCreator({ account, resourceId, resourceType, tags: tagIds, attributes: attributeIds, update, visible, createTagsOrAttributesSuccess, createTagsOrAttributesError, createTagsOrAttributesCancel }) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState({})
  const [updating, setUpdating] = useState(false)
  const type = attributeIds ? "attribute" : "tag"

  // Need to listen on redux state changes that affect theses props
  useEffect(() => setOpen(visible), [visible])
  useEffect(() => setItems(reduce(attributeIds ?? tagIds, (acc, item) => set(acc, item, defaultTag(item)), {})), [attributeIds, tagIds])

  const makeItemOnChange = (item) => (field) => (e) => {
    const path = field === "checked" ? "target.checked" : "target.value"
    setItems(set({ ...items }, [item, field], get(e, path)))
  }

  // Disabled if its updating, a checked item is invalid or no items are checked
  const isDisabled = () => updating || some(items, item => item.checked && !validName(item.name)) || every(items, item => !item.checked)

  const updateCallback = (errors) => {
    setUpdating(false)
    if (isEmpty(errors)) {
      createTagsOrAttributesSuccess()
    } else {
      createTagsOrAttributesError()
    }
  }

  const submit = () => {
    const itemsToCreate = reduce(items, (acc, item) => item.checked ? {
      ...acc,
      [item.id]: omitBy(item, validTagKeys),
    } : acc, {})

    let query
    if (type === "attribute") {
      query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType,${map(itemsToCreate, (attribute) => ` $${attribute.id}: PositionAttributeCategoryInput!`)}){
        ${map(itemsToCreate, ({ id }) => `attribute${id}: createPositionAttributeCategory(resourceId: $resourceId, resourceType: $resourceType, category: $${id}){ id }`)}
      }`
    } else {
      query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType,${map(itemsToCreate, (tag) => ` $${tag.id}: TagCategoryInput!`)}){
      ${map(itemsToCreate, ({ id }) => `tag${id}: createTagCategory(resourceId: $resourceId, resourceType: $resourceType, category: $${id}){ id }`)}
    }`
    }

    const variables = {
      resourceId,
      resourceType,
      ...itemsToCreate,
    }

    setUpdating(true)
    update(query, variables, account, {}, updateCallback)
  }

  const ignoreAll = () => {
    createTagsOrAttributesSuccess()
  }

  const cancel = () => {
    createTagsOrAttributesCancel()
  }

  return <Dialog className="tag-and-attribute-creator" onClose={cancel} open={open}>
    <DialogTitle>Initialize your {type} categories</DialogTitle>
    <DialogContent>
      <Typography variant="body1">We have detected the following <b>uninitialized</b> {type} categories while scanning your files. Uncheck {type}s you do not want to create</Typography>
      {tagIds && attributeIds && <Alert className="helper-text" severity="warning">We have also detected tags while scanning your files. Since you are using position attributes, tag columns will be ignored.</Alert>}
      <Alert className="helper-text" severity="info">Name should be between 3 and 50 characters, with alphanumeric characters, underscores, dashes and spaces allowed.</Alert>
      <FormGroup className="items">
        {map(items, (item, itemId) => <Item key={itemId} item={item} type={type} makeOnChange={makeItemOnChange(itemId)} />)}
      </FormGroup>
    </DialogContent>
    <DialogActions>
      <Button onClick={cancel}>Cancel</Button>
      <Button className="danger" onClick={ignoreAll}>Ignore all</Button>
      <Button color="primary" autoFocus onClick={submit} disabled={isDisabled()}>Create selected</Button>
    </DialogActions>
  </Dialog>
}

TagAndAttributeCreator.propTypes = {
  account: PropTypes.string.isRequired,
  resourceId: PropTypes.string.isRequired,
  resourceType: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  attributes: PropTypes.arrayOf(PropTypes.string),
  update: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  createTagsOrAttributesSuccess: PropTypes.func.isRequired,
  createTagsOrAttributesError: PropTypes.func.isRequired,
  createTagsOrAttributesCancel: PropTypes.func.isRequired,
}
